import { useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Layout } from "../components/Layout"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { CommsTabs, ChannelPill } from "@/components/CommsTabs"
import { parseCommsChannel } from "@/lib/commsChannel"
import {
  useCommsThreads,
  useResolveThreadsBatch,
  useSetThreadResolved,
  ThreadListItem,
  formatAge,
} from "@/hooks/useComms"
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"

// Latest touch = the LATER of inbound/outbound (ISO strings sort
// lexicographically), so a fresh outbound reply doesn't read as stale.
function latestTouch(t: ThreadListItem): string | null {
  return (
    [t.last_inbound_at, t.last_outbound_at].filter((x): x is string => !!x).sort().pop() ?? null
  )
}

// active = open threads; resolved = the archive; bucket = quarantined ("Not relevant").
type View = "active" | "resolved" | "bucket"
const CHANNEL_TITLES = {
  support: "Support",
  legal: "Legal",
  voicemail: "Voicemail",
  primary: "Primary",
} as const

export function CommsThreads() {
  const navigate = useNavigate()
  const [view, setView] = useState<View>("active")
  // Channel lens lives in the URL (?channel=), driven by the CommsTabs bar. Unknown = all.
  const [searchParams] = useSearchParams()
  const channel = parseCommsChannel(searchParams.get("channel"))
  const { data, isLoading, error } = useCommsThreads({
    limit: 200,
    ...(view === "bucket" ? { bucket: "not_relevant" as const } : {}),
    ...(view === "resolved" ? { bucket: "resolved" as const } : {}),
    ...(channel ? { channel } : {}),
  })
  const setResolved = useSetThreadResolved()
  const archiveBatch = useResolveThreadsBatch()

  const columns = useMemo<ColumnDef<ThreadListItem>[]>(
    () => [
      {
        accessorKey: "subject",
        header: "Subject",
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-primary">
              {row.original.subject || "(no subject)"}
            </p>
            <p className="text-xs text-muted-foreground">{row.original.message_count} messages</p>
          </div>
        ),
      },
      {
        id: "channel",
        header: "Channel",
        cell: ({ row }) => (
          <ChannelPill
            channel={row.original.channel}
            isLegal={row.original.is_legal}
            showPrimary
          />
        ),
      },
      {
        id: "contact",
        header: "Contact",
        cell: ({ row }) => {
          const c = row.original.contact
          return c ? (
            <div className="min-w-0">
              <p className="truncate text-sm text-primary">{c.display_name || c.primary_email}</p>
              {c.org && <p className="truncate text-xs text-muted-foreground">{c.org}</p>}
            </div>
          ) : (
            <span className="text-xs italic text-muted-foreground">unknown</span>
          )
        },
      },
      {
        id: "activity",
        header: "Last activity",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {formatAge(latestTouch(row.original))}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) =>
          row.original.resolved_at ? (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              resolved
            </span>
          ) : row.original.still_owed ? (
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              owed
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: "archive",
        header: "",
        cell: ({ row }) => {
          const resolved = !!row.original.resolved_at
          return (
            <button
              type="button"
              disabled={setResolved.isPending}
              // The row is a link to the thread; don't navigate when archiving.
              onClick={(e) => {
                e.stopPropagation()
                setResolved.mutate({ publicId: row.original.public_id, resolved: !resolved })
              }}
              className="rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-primary disabled:opacity-50"
            >
              {resolved ? "Reopen" : "Archive"}
            </button>
          )
        },
      },
    ],
    [setResolved]
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  const threads = data?.threads ?? []
  const total = data?.total ?? threads.length
  // The table search is client-side over the loaded page; if more threads exist
  // than were loaded, say so rather than implying the list is complete.
  const subtitle = isLoading
    ? "Loading…"
    : total > threads.length
      ? `Showing first ${threads.length} of ${total} — search covers the loaded rows`
      : `${total} ${channel ? `${CHANNEL_TITLES[channel].toLowerCase()} ` : "captured "}${total === 1 ? "thread" : "threads"}`

  return (
    <Layout>
      <div className="space-y-6">
        <CommsTabs />
        <div>
          <h1 className="text-2xl font-bold text-primary">
            Comms · {channel ? CHANNEL_TITLES[channel] : "Threads"}
          </h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Active vs. "Not relevant" bucket toggle — keeps quarantined threads
              (incl. zero-message ones) discoverable; open one to Restore it. */}
          <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 text-sm">
            <button
              type="button"
              onClick={() => setView("active")}
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                view === "active"
                  ? "bg-primary text-contrast"
                  : "text-muted-foreground hover:text-primary"
              }`}
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setView("resolved")}
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                view === "resolved"
                  ? "bg-primary text-contrast"
                  : "text-muted-foreground hover:text-primary"
              }`}
            >
              Archived
            </button>
            <button
              type="button"
              onClick={() => setView("bucket")}
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                view === "bucket"
                  ? "bg-primary text-contrast"
                  : "text-muted-foreground hover:text-primary"
              }`}
            >
              Not relevant
            </button>
          </div>
          {/* Clear the board: archives every OPEN thread currently loaded. A newer
              inbound reopens a thread by itself, so this never hides a live reply. */}
          {view === "active" && threads.length > 0 && (
            <button
              type="button"
              disabled={archiveBatch.isPending}
              onClick={() => {
                const ids = threads.filter((t) => !t.resolved_at).map((t) => t.public_id)
                if (ids.length === 0) return
                if (
                  window.confirm(
                    `Archive ${ids.length} open thread${ids.length === 1 ? "" : "s"}? A new reply reopens one automatically.`
                  )
                ) {
                  // The endpoint takes at most 90 per call; chunk the loaded page.
                  for (let i = 0; i < ids.length; i += 90) {
                    archiveBatch.mutate({ publicIds: ids.slice(i, i + 90) })
                  }
                }
              }}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-primary disabled:opacity-50"
            >
              Archive these {threads.length}
            </button>
          )}
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : threads.length === 0 ? (
          <EmptyState
            icon={<ChatBubbleLeftRightIcon className="h-12 w-12" />}
            title={
              view === "bucket"
                ? "Nothing quarantined"
                : view === "resolved"
                  ? "Nothing archived"
                  : "No open threads"
            }
            description={
              view === "bucket"
                ? "Threads marked “Not relevant” appear here — open one to Restore it."
                : view === "resolved"
                  ? "Resolved threads — and outreach nobody answered for 21 days — land here."
                  : "Captured email threads will appear here."
            }
          />
        ) : (
          <DataTable
            columns={columns}
            data={threads}
            searchKey="subject"
            onRowClick={(row) => navigate(`/comms/threads/${row.public_id}`)}
          />
        )}
      </div>
    </Layout>
  )
}
